import React from "react"
import { observer } from "mobx-react-lite"
import { useAlert } from "react-alert"

import AdminLayout from "@components/layout/AdminLayout/AdminLayout"
import EntityPageHeaderLayout from "@components/layout/EntityPageHeaderLayout/EntityPageHeaderLayout"
import Button from "@components/ui/Button/Button"
import Icon from "@components/ui/Icon/Icon"
import Chip from "@components/ui/Chip/Chip"
import Loader from "@components/ui/Loader/BarLoader"
import NotFound from "@components/ui/NotFound/NotFound"
import Select from "@components/ui/MultiSelect/Select"
import Label from "@components/ui/Label/Label"
import List from "@components/ui/List/List"
import Text from "@components/ui/Typography/Text"
import BackButton from "@components/prototypes/BackButton"
import { useController, useStore } from "@store/index"
import { BrandExpertRole } from "@framework/types/brand-expert"
import useExpertsList from "@components/modals/AssignExpertToQuestionModal/useExpertsList"

import styles from "./BrandExperts.module.sass"

/**
 * LAB-47: brand to expert routing maintenance.
 *
 * Exists so the mapping can be kept current without a developer - the initial
 * ~40 brands are loaded from the specialist spreadsheet by a one-off script,
 * and everything after that is edited here.
 */
export const BrandExpertsPage: React.FC = observer(() => {
  const alert = useAlert()

  const {
    brandExpertStore: {
      mappingsByBrand,
      isMappingsLoading,
      isActionLoading,
      error,
      loadMappings,
      addMapping,
      updateMapping,
      removeMapping,
    },
    companyStore: { companies, loadingCompanies },
  } = useStore()

  const { companyController } = useController()

  const experts = useExpertsList()

  const [companyId, setCompanyId] = React.useState<string | null>(null)
  const [userId, setUserId] = React.useState<string | null>(null)
  const [role, setRole] = React.useState<BrandExpertRole>(
    BrandExpertRole.PRIMARY
  )

  React.useEffect(() => {
    loadMappings()
    companyController.fetchCompanies()
  }, [])

  React.useEffect(() => {
    if (error) alert.error(error)
  }, [error])

  // The picker lists every manufacturer, not just the routable ones - a brand
  // with no expert yet is exactly what this screen exists to fix.
  const companyOptions = React.useMemo(
    () =>
      companies.map((company) => ({ name: company.id, value: company.name })),
    [companies]
  )

  const expertOptions = React.useMemo(
    () =>
      experts.list.map((expert) => ({
        name: expert.id,
        value: `${expert.fullName} (${expert.email})`,
      })),
    [experts.list]
  )

  const handleAdd = async () => {
    if (!companyId || !userId) return

    const success = await addMapping({ companyId, userId, role })

    if (success) {
      alert.success("Expert added")
      setUserId(null)
    }
  }

  const handleMakePrimary = async (id: string) => {
    const success = await updateMapping(id, { role: BrandExpertRole.PRIMARY })
    if (success) alert.success("Primary expert updated")
  }

  const handleRemove = async (id: string) => {
    const success = await removeMapping(id)
    if (success) alert.success("Expert removed")
  }

  return (
    // AdminLayout is a 3-row grid; EntityPageHeaderLayout is the header bar only
    // (its children land in a 300px centre slot), so page content goes beside it,
    // not inside it.
    <AdminLayout>
      <EntityPageHeaderLayout
        className={styles.header}
        left={<BackButton to="/admin">Brand experts</BackButton>}
      />

      <div className={styles.form}>
        <Text variant="body2" className={styles.intro}>
          Questions posted to an expert are routed to the brand&apos;s primary
          expert. Secondary experts are contacted only if a question is still
          unanswered after 10 working days. Brands without a primary expert are
          not offered in the Ask an Expert form.
        </Text>

        <div className={styles.addRow}>
          <Label id="brand_expert_brand" label="Brand">
            <Select
              clearable
              searchable
              placeholder="Select a brand"
              loading={loadingCompanies}
              options={companyOptions}
              value={companyId}
              onChange={setCompanyId}
            />
          </Label>

          <Label id="brand_expert_user" label="Expert">
            <Select
              clearable
              searchable
              placeholder="Select an expert"
              loading={experts.isLoading}
              options={expertOptions}
              value={userId}
              onChange={setUserId}
            />
          </Label>

          <Label id="brand_expert_role" label="Role">
            <Select
              options={[
                { name: BrandExpertRole.PRIMARY, value: "Primary" },
                { name: BrandExpertRole.SECONDARY, value: "Secondary" },
              ]}
              value={role}
              onChange={(value) =>
                setRole((value as BrandExpertRole) || BrandExpertRole.PRIMARY)
              }
            />
          </Label>

          <Button
            color="primary"
            disabled={!companyId || !userId || isActionLoading}
            onClick={handleAdd}
            before={<Icon name="plus" />}
          >
            Add
          </Button>
        </div>
      </div>

      {isMappingsLoading ? (
        <Loader size="medium" fluid />
      ) : (
        <List gutter="16" className={styles.brandList}>
          {mappingsByBrand.length === 0 ? (
            <NotFound>No brand to expert mappings configured yet</NotFound>
          ) : (
            mappingsByBrand.map((brand) => (
              <div className={styles.brandCard} key={brand.companyId}>
                <Text variant="h4">{brand.companyName}</Text>

                {brand.experts.map((mapping) => (
                  <div className={styles.expertRow} key={mapping.id}>
                    <span className={styles.expertName}>
                      {mapping.expert?.name || "Deactivated user"}
                      <Text variant="caption2" color="text50Color">
                        {mapping.expert?.email}
                      </Text>
                    </span>

                    <Chip
                      color={
                        mapping.role === BrandExpertRole.PRIMARY
                          ? "green-solid"
                          : "secondary-solid"
                      }
                      variant="rounded"
                    >
                      {mapping.role === BrandExpertRole.PRIMARY
                        ? "Primary"
                        : "Secondary"}
                    </Chip>

                    {mapping.role !== BrandExpertRole.PRIMARY && (
                      <Button
                        size="tiny"
                        color="secondary"
                        disabled={isActionLoading}
                        onClick={() => handleMakePrimary(mapping.id)}
                      >
                        Make primary
                      </Button>
                    )}

                    <Button
                      size="tiny"
                      color="secondary"
                      disabled={isActionLoading}
                      before={<Icon name="trash-can" />}
                      onClick={() => handleRemove(mapping.id)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            ))
          )}
        </List>
      )}
    </AdminLayout>
  )
})

export default BrandExpertsPage
